import React, { useEffect, useState } from 'react';

import http from '../../../api/http';
import RecordSection from '../../view/RecordSection';
import StatusBadge from '../../ui/StatusBadge';

const messageOf = (error, fallback) => error?.response?.data?.message || error?.message || fallback;

// `offer` is set when a status change was refused with `approvalRequired`.
const RecordApprovalsSection = ({ slug, id, offer = null, onOfferHandled, onApplied }) => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    http.get('/approvals', { params: { model: slug, recordId: id, status: 'pending' } })
      .then(({ data }) => { if (active) setItems(Array.isArray(data?.items) ? data.items : []); })
      .catch((requestError) => { if (active) setError(messageOf(requestError, 'Unable to load approvals.')); });
    return () => { active = false; };
  }, [slug, id, reloadKey]);

  async function requestApproval(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await http.post('/approvals', { model: slug, id, to: offer.to, note });
      setNote('');
      onOfferHandled?.();
      setReloadKey((key) => key + 1);
    } catch (requestError) {
      setError(messageOf(requestError, 'Unable to request approval.'));
    } finally {
      setBusy(false);
    }
  }

  async function decide(approvalId, decision) {
    setBusy(true);
    setError('');
    try {
      const { data } = await http.post(`/approvals/${encodeURIComponent(approvalId)}/decide`, { decision });
      if (data?.status === 'applied') onApplied?.();
      if (data?.status === 'stale') setError('The record changed status before approval completed; the request is now stale.');
      if (data?.status === 'failed') setError('Approval completed but the transition could not be applied.');
      setReloadKey((key) => key + 1);
    } catch (requestError) {
      setError(messageOf(requestError, 'Unable to record decision.'));
    } finally {
      setBusy(false);
    }
  }

  if (!offer && !items.length && !error) return null;

  return (
    <RecordSection title="Approvals" className="record-approvals">
      {error ? <p className="error-text" role="alert">{error}</p> : null}

      {offer ? (
        <form className="record-links-form" onSubmit={requestApproval}>
          <span>
            Moving to <StatusBadge value={offer.to} /> needs {offer.count} approval{offer.count === 1 ? '' : 's'} from a {offer.role} or above.
          </span>
          <input
            type="text"
            value={note}
            maxLength={2000}
            placeholder="Note for approvers (optional)"
            onChange={(event) => setNote(event.target.value)}
          />
          <button className="button-primary" type="submit" disabled={busy}>Request approval</button>
          <button className="button-secondary" type="button" onClick={() => onOfferHandled?.()} disabled={busy}>Dismiss</button>
        </form>
      ) : null}

      {items.length ? (
        <ul>
          {items.map((item) => {
            const approvals = (item.decisions || []).filter((entry) => entry.decision === 'approve').length;
            return (
              <li key={item.id}>
                <StatusBadge value={item.fromStatus} /> → <StatusBadge value={item.toStatus} />{' '}
                <span className="small-muted">
                  {approvals}/{item.requiredCount} approvals · requires {item.requiredRole}+
                  {item.note ? ` · “${item.note}”` : ''}
                </span>{' '}
                <button className="button-primary" type="button" disabled={busy} onClick={() => decide(item.id, 'approve')}>Approve</button>{' '}
                <button className="button-secondary" type="button" disabled={busy} onClick={() => decide(item.id, 'reject')}>Reject</button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </RecordSection>
  );
};

export default RecordApprovalsSection;
